import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { LocationChecklist } from "./LocationChecklist";
import {
  ORGANIZATION_LOCATIONS_MAX,
  ORGANIZATION_LOCATIONS_PAGE_SIZE,
  ORGANIZATION_LOCATIONS_SEARCH_MAX_LENGTH,
} from "./location-limits";
import { useOrganizationLocations } from "./use-organization-locations";

const SEARCH_DEBOUNCE_MS = 300;

interface LocationPickerProps {
  readonly organizationId: string;
  /** Ticked ids; they survive searching and paging because the caller owns them. */
  readonly selected: ReadonlySet<string>;
  /** Receives the name as well, so the caller can still name what it ticked after paging away. */
  readonly onToggle: (id: string, name: string) => void;
  /** Ids that cannot be toggled (already assigned...). */
  readonly disabledIds?: readonly string[];
  /** The whole picker is read-only (a write is in flight). */
  readonly disabled?: boolean;
  /** Test ids: `${p}-locations-search`, `${p}-location-${id}`, `${p}-no-locations`... */
  readonly testIdPrefix: string;
}

/**
 * Searchable, paged checklist of the locations of ONE organization, capped at
 * the API's per-write limit (100): at the cap the unticked ones are blocked
 * and a hint says why, instead of letting the API answer 400.
 */
export function LocationPicker({
  organizationId,
  selected,
  onToggle,
  disabledIds = [],
  disabled = false,
  testIdPrefix,
}: LocationPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounced = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const { locations, totalCount, hasNextPage, isLoading, isError, isFetching, refetch } =
    useOrganizationLocations({
      organizationId,
      enabled: true,
      search: debounced,
      page,
    });

  const capReached = selected.size >= ORGANIZATION_LOCATIONS_MAX;
  const blockedIds = disabled
    ? locations.map((l) => l.id)
    : [
        ...disabledIds,
        ...(capReached ? locations.filter((l) => !selected.has(l.id)).map((l) => l.id) : []),
      ];
  const showPager = page > 1 || hasNextPage;
  const pageCount = Math.max(1, Math.ceil(totalCount / ORGANIZATION_LOCATIONS_PAGE_SIZE));

  return (
    <div className="space-y-2">
      <Input
        data-testid={`${testIdPrefix}-locations-search`}
        type="search"
        value={search}
        disabled={disabled}
        maxLength={ORGANIZATION_LOCATIONS_SEARCH_MAX_LENGTH}
        placeholder={t("organizationAccess.locations.searchPlaceholder")}
        aria-label={t("organizationAccess.locations.searchLabel")}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
      />
      {isLoading && (
        <p
          data-testid={`${testIdPrefix}-locations-loading`}
          className="text-sm text-muted-foreground"
        >
          {t("organizationAccess.locations.loading")}
        </p>
      )}
      {isError && (
        <div className="flex flex-wrap items-center gap-2">
          <p data-testid={`${testIdPrefix}-locations-error`} className="text-sm text-destructive">
            {t("organizationAccess.locations.loadError")}
          </p>
          <Button
            data-testid={`${testIdPrefix}-locations-retry`}
            type="button"
            variant="outline"
            size="sm"
            onClick={refetch}
          >
            {t("organizationAccess.retry")}
          </Button>
        </div>
      )}
      {!isLoading && !isError && locations.length === 0 && (
        <p data-testid={`${testIdPrefix}-no-locations`} className="text-sm text-muted-foreground">
          {t(
            debounced.trim()
              ? "organizationAccess.locations.noMatches"
              : "organizationAccess.locations.none",
          )}
        </p>
      )}
      {locations.length > 0 && (
        // `keepPreviousData` leaves the previous rows on screen while the next
        // search or page loads: dimmed and busy, so they do not pass for current.
        <div
          data-testid={`${testIdPrefix}-locations-list`}
          aria-busy={isFetching}
          className={isFetching ? "opacity-60 transition-opacity" : undefined}
        >
          <LocationChecklist
            locations={locations}
            selected={selected}
            onToggle={onToggle}
            disabledIds={blockedIds}
            testIdPrefix={`${testIdPrefix}-location`}
            label={t("organizationAccess.locations.label")}
          />
        </div>
      )}
      {showPager && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <Button
            data-testid={`${testIdPrefix}-locations-prev`}
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t("organizationAccess.locations.previous")}
          </Button>
          <span>{t("organizationAccess.locations.pageOf", { page, pages: pageCount })}</span>
          <Button
            data-testid={`${testIdPrefix}-locations-next`}
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || !hasNextPage}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("organizationAccess.locations.next")}
          </Button>
        </div>
      )}
      {capReached && (
        <p data-testid={`${testIdPrefix}-locations-cap`} className="text-xs text-muted-foreground">
          {t("organizationAccess.locations.capReached", { max: ORGANIZATION_LOCATIONS_MAX })}
        </p>
      )}
    </div>
  );
}
